/**
 * "Recommended for you" — pure venue picker for the signed-in strip.
 *
 * Blends three signals into one deterministic top-N:
 *   1. Visit history + feedback ratings (`my_visits` RPC) — the strong signal.
 *      Each visit votes on the venue itself AND on its cuisines/tags, weighted
 *      by rating and recency, so the profile transfers to venues the diner has
 *      never opened. Taxonomy is resolved against the passed-in venue list at
 *      call time — new venues, cuisines, and tags join the pool automatically.
 *   2. The on-device tap history (lib/tapHistory.ts), reusing the feed's
 *      `scoreByHistory` — deliberately a nudge, not a takeover.
 *   3. A fallback fill so the strip is never empty while the city has venues:
 *      open venues first (paying `pos_enabled` before basic), then closed, A→Z.
 *
 * An OPTIONAL fourth signal (It-7, beta) rides on top: an embedding-similarity
 * index (lib/similarVenues.ts) spreading each visit's vote onto venues that
 * *read* like the visited one — it catches kinship the slug taxonomy misses
 * (a natural-wine bistro and a small-plates place sharing no cuisine slug).
 * It is absent unless the caller passes it, and "absent" means the term is
 * never added, so the flag-off output is identical to the pre-It-7 engine
 * rather than merely close to it.
 *
 * Openness dominates everything: an open venue always ranks before a closed
 * one, however strong the closed venue's personal score (Teo's call, Jul 17 —
 * "recommended right now" must be actionable right now).
 *
 * Pure and fail-soft by construction: no I/O, no throwing — malformed dates,
 * unknown venue ids (deleted venues, visits from another city), missing slug
 * arrays, and out-of-range ratings all degrade to weaker signal, never errors.
 */

import { scoreByHistory } from "@/lib/feedFilter";
import type { SimilarityIndex } from "@/lib/similarVenues";
import type { TapEntry } from "@/lib/tapHistory";
import type { MyVisit } from "@/lib/visits";
import type { Restaurant } from "@/types/restaurant";

/** A venue + the feed's open flag, in strip-ready order. */
export interface RecommendedItem {
  r: Restaurant;
  open: boolean;
}

export interface RecommendationResult {
  /** Up to `max` venues, ready for the strip. */
  items: RecommendedItem[];
  /**
   * True when at least one pick was driven by a positive personal signal
   * (visits/ratings/taps) — gates the "based on your visits" subtext so we
   * never claim personalization we didn't do (truthful-claims rule).
   */
  personalized: boolean;
}

// Visits are a stronger, slower-decaying signal than taps (14d in feedFilter).
const VISIT_HALF_LIFE_DAYS = 30;
// Weight of a visit when the age can't be parsed: "old-ish", not discarded.
const UNPARSABLE_AGE_WEIGHT = 0.25;
// A tag says less about taste than a cuisine (mirrors feedFilter's 1 : 0.5).
const TAG_VS_CUISINE = 0.5;
// Blend weights: taste-match dominates, proven favorites bounded, taps nudge.
const TASTE_WEIGHT = 2;
const FAVORITE_WEIGHT = 1;
const TAP_WEIGHT = 0.5;
// Embedding similarity sits between a tap and the taxonomy taste it supplements:
// it can surface a venue the slugs would have missed, but it cannot outvote what
// the diner actually rated.
const SIMILARITY_WEIGHT = 0.75;
// One beloved venue can't monopolize the score; one bad one can't nuke a slug.
const FAVORITE_CLAMP_MIN = -2;
const FAVORITE_CLAMP_MAX = 1.5;
// At most this many already-visited venues while unvisited matches remain —
// the strip stays mostly discovery, favorites keep it grounded.
const MAX_FAVORITES = 3;
// Each already-picked venue sharing a cuisine decays the next one's score:
// a pizza-loving profile still gets pizza, just not ten in a row.
const DIVERSITY_DECAY = 0.7;
const EPS = 1e-9;

/** How strongly a visit votes: loved it → +1, hated it → −1, no rating → mild +. */
const RATING_FACTORS = [-1, -0.6, 0.15, 0.6, 1] as const;

function ratingFactor(rating: number | null): number {
  if (rating === null || !Number.isFinite(rating)) return 0.3;
  const r = Math.min(5, Math.max(1, Math.round(rating)));
  return RATING_FACTORS[r - 1] ?? 0.3;
}

/**
 * Pick up to `max` venues for the signed-in diner. `restaurants` is the feed's
 * `orderedRestaurants` verbatim; `now` is epoch ms. Deterministic: same inputs
 * → same output.
 *
 * `similarity` is the optional It-7 signal (undefined = off, and off is a real
 * absence — see the module comment).
 */
export function recommendVenues(
  restaurants: RecommendedItem[],
  visits: MyVisit[],
  tapHistory: TapEntry[],
  now: number,
  max = 10,
  similarity?: SimilarityIndex,
): RecommendationResult {
  if (restaurants.length === 0) return { items: [], personalized: false };

  const byId = new Map(restaurants.map((item) => [item.r.id, item]));

  // -- Taste profile from visits --------------------------------------------
  const venueAffinity = new Map<string, number>();
  const cuisineAffinity = new Map<string, number>();
  const tagAffinity = new Map<string, number>();
  // It-7: only populated when a similarity index was passed in.
  const similarityAffinity = new Map<string, number>();
  // Latest rating per venue drives the hard exclusion below. `my_visits`
  // returns newest-first, but don't rely on it — track the max timestamp.
  const latestRating = new Map<string, { at: number; rating: number | null }>();

  for (const visit of visits) {
    const at = Date.parse(visit.detected_at);
    const ageDays = Number.isNaN(at) ? NaN : Math.max(0, (now - at) / 86_400_000);
    const weight = Number.isNaN(ageDays)
      ? UNPARSABLE_AGE_WEIGHT
      : 0.5 ** (ageDays / VISIT_HALF_LIFE_DAYS);
    const factor = ratingFactor(visit.rating);

    venueAffinity.set(visit.venue_id, (venueAffinity.get(visit.venue_id) ?? 0) + factor * weight);
    const prev = latestRating.get(visit.venue_id);
    const ts = Number.isNaN(at) ? 0 : at;
    if (!prev || ts > prev.at) latestRating.set(visit.venue_id, { at: ts, rating: visit.rating });

    // It-7: spread this visit's vote onto embedding-similar venues, scaled by
    // how alike they are. Deliberately BEFORE the byId guard — neighbours are
    // computed server-side across the whole visible catalog, so a visit to a
    // venue that isn't in this city's list can still vouch for one that is.
    // A bad rating propagates negatively, symmetric with the taxonomy votes.
    if (similarity) {
      for (const [neighborId, score] of similarity.get(visit.venue_id) ?? []) {
        if (neighborId === visit.venue_id) continue;
        similarityAffinity.set(
          neighborId,
          (similarityAffinity.get(neighborId) ?? 0) + factor * weight * score,
        );
      }
    }

    // Taxonomy votes need the venue in the current list; visits to deleted or
    // out-of-city venues still count via venueAffinity, just not by taste.
    const visited = byId.get(visit.venue_id);
    if (!visited) continue;
    for (const slug of visited.r.cuisine_slugs ?? []) {
      cuisineAffinity.set(slug, (cuisineAffinity.get(slug) ?? 0) + factor * weight);
    }
    for (const slug of visited.r.tag_slugs ?? []) {
      tagAffinity.set(slug, (tagAffinity.get(slug) ?? 0) + factor * weight * TAG_VS_CUISINE);
    }
  }

  // -- Score every candidate --------------------------------------------------
  const scored = restaurants
    // Hard exclusion: their latest word on this venue was 1–2 stars.
    .filter((item) => {
      const last = latestRating.get(item.r.id);
      return !(last && last.rating !== null && Number.isFinite(last.rating) && last.rating <= 2);
    })
    .map((item) => {
      let taste = 0;
      for (const slug of item.r.cuisine_slugs ?? []) taste += cuisineAffinity.get(slug) ?? 0;
      for (const slug of item.r.tag_slugs ?? []) taste += tagAffinity.get(slug) ?? 0;
      const favorite = Math.min(
        FAVORITE_CLAMP_MAX,
        Math.max(FAVORITE_CLAMP_MIN, venueAffinity.get(item.r.id) ?? 0),
      );
      // Visit/rating-driven part of the score, kept separate so the "based on
      // your visits" subtext can be gated on it — tap-only picks are still
      // personalized-ish, but the copy would overclaim (truthful-claims rule).
      // The It-7 term belongs here: it is derived from the same visits, so a
      // pick it drives really is "based on your visits". The term is added only
      // when the signal was supplied, never as `+ 0.75 * 0` — flag-off output
      // is then identical to the pre-It-7 engine, not just numerically close.
      let visitScore = TASTE_WEIGHT * taste + FAVORITE_WEIGHT * favorite;
      if (similarity) {
        visitScore += SIMILARITY_WEIGHT * (similarityAffinity.get(item.r.id) ?? 0);
      }
      const score = visitScore + TAP_WEIGHT * scoreByHistory(item.r, tapHistory, now);
      return { item, score, visitScore, isFavorite: (venueAffinity.get(item.r.id) ?? 0) > 0 };
    });

  // Deterministic base order: score, then the grid's own tiebreak stack.
  scored.sort(
    (a, b) =>
      b.score - a.score ||
      Number(b.item.r.pos_enabled === true) - Number(a.item.r.pos_enabled === true) ||
      a.item.r.name.localeCompare(b.item.r.name) ||
      a.item.r.id.localeCompare(b.item.r.id),
  );

  // -- Two openness tiers: open venues fill first, closed only take leftovers -
  // Within a tier: greedy personalized picks (diversity decay + favorites cap),
  // then a fallback fill (paying venues first, then A→Z).
  const picked: RecommendedItem[] = [];
  const pickedIds = new Set<string>();
  let visitDrivenPick = false;

  const pickTier = (tier: typeof scored) => {
    const cuisineCounts = new Map<string, number>();
    let favoritesPicked = 0;
    const positives = tier.filter((c) => c.score > EPS);
    while (picked.length < max && positives.length > 0) {
      const remaining = positives.filter((c) => !pickedIds.has(c.item.r.id));
      if (remaining.length === 0) break;
      // Enforce the favorites cap only while an unvisited match is still on
      // the table — never leave slots empty just to honor the cap.
      const capped =
        favoritesPicked >= MAX_FAVORITES && remaining.some((c) => !c.isFavorite)
          ? remaining.filter((c) => !c.isFavorite)
          : remaining;
      // Diversity decay re-evaluates on every pick; ties keep the base order.
      const first = capped[0];
      if (!first) break;
      let best = first;
      let bestEffective = -Infinity;
      for (const c of capped) {
        let repeats = 0;
        for (const slug of c.item.r.cuisine_slugs ?? []) {
          repeats = Math.max(repeats, cuisineCounts.get(slug) ?? 0);
        }
        const effective = c.score * DIVERSITY_DECAY ** repeats;
        if (effective > bestEffective + EPS) {
          best = c;
          bestEffective = effective;
        }
      }
      picked.push(best.item);
      pickedIds.add(best.item.r.id);
      if (best.visitScore > EPS) visitDrivenPick = true;
      if (best.isFavorite) favoritesPicked += 1;
      for (const slug of best.item.r.cuisine_slugs ?? []) {
        cuisineCounts.set(slug, (cuisineCounts.get(slug) ?? 0) + 1);
      }
    }
    // Fallback fill within the tier: paying venues first, then A→Z.
    if (picked.length < max) {
      const fill = tier
        .filter((c) => !pickedIds.has(c.item.r.id))
        .sort(
          (a, b) =>
            Number(b.item.r.pos_enabled === true) - Number(a.item.r.pos_enabled === true) ||
            a.item.r.name.localeCompare(b.item.r.name) ||
            a.item.r.id.localeCompare(b.item.r.id),
        );
      for (const c of fill) {
        if (picked.length >= max) break;
        picked.push(c.item);
        pickedIds.add(c.item.r.id);
      }
    }
  };

  pickTier(scored.filter((c) => c.item.open));
  pickTier(scored.filter((c) => !c.item.open));

  return { items: picked, personalized: visitDrivenPick };
}
